import React, { useState, useEffect } from 'react';
import axios from '../../axiosConfig';
import {
  Typography,
  Box,
  Grid,
  CircularProgress,
  LinearProgress,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { acme } from '../../theme/acmeTheme';
import {
  StatCard,
  Card,
  CardContent,
  Button,
  Table,
  DashboardMetricIcon,
  projectStatusIcons,
} from '../ui';
import { ClipboardList } from 'lucide-react';

const panelCardSx = { height: '100%', boxShadow: 'none' };

const changeIndicatorSx = (positive) => ({
  position: 'absolute',
  top: 12,
  right: 12,
  borderRadius: acme.radius.sm,
  px: 1,
  py: 0.25,
  fontSize: '0.75rem',
  fontWeight: 700,
  backgroundColor: positive ? '#dcfce7' : '#fee2e2',
  color: positive ? '#166534' : '#991b1b',
});

const progressBarSx = {
  height: 10,
  borderRadius: acme.radius.full,
  backgroundColor: acme.colors.muted,
  '& .MuiLinearProgress-bar': {
    backgroundColor: acme.colors.iconDefault,
    borderRadius: acme.radius.full,
  },
};

const DashboardProject = ({ dateRange, calculateChange, calculateRate }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projectStats, setProjectStats] = useState({
    new: { count: 0, rate: 0, change: 0, loading: false },
    pending: { count: 0, rate: 0, change: 0, loading: false },
    communication: { count: 0, rate: 0, change: 0, loading: false },
    pendingPayment: { count: 0, rate: 0, change: 0, loading: false },
    inProcessing: { count: 0, rate: 0, change: 0, loading: false },
    closed: { count: 0, rate: 0, change: 0, loading: false }
  });
  const [projects, setProjects] = useState([]);
  const [financialStats, setFinancialStats] = useState({
    paid: { amount: 0, rate: 0, change: 0, loading: false, currency: '€' },
    pendingPayment: { amount: 0, rate: 0, change: 0, loading: false, currency: '€' }
  });

  // Status mapping for progress calculation
  const statusValues = {
    'New': 0,
    'Pending': 20,
    'Communication': 40,
    'Pending payment': 60,
    'In processing': 80,
    'Closed': 100
  };

  // Function to calculate progress percentage based on status
  const calculateProgress = (status) => {
    return statusValues[status] || 0;
  };

  // Fetch projects data
  const fetchProjects = async () => {
    try {
      setLoading(true);
      
      // Get all projects (no date filtering) - credentials included automatically
      const response = await axios.get('/api/requests');
      
      console.log('Fetched projects data:', response.data);
      console.log('Total projects count:', response.data.length);
      
      const projectsData = response.data;
      
      // Sort projects by completion percentage (descending)
      const sortedProjects = projectsData.sort((a, b) => {
        const progressA = calculateProgress(a.status);
        const progressB = calculateProgress(b.status);
        return progressB - progressA;
      });
      
      // Get top 10 projects
      const topProjects = sortedProjects.slice(0, 10);
      
      console.log('Top projects to display:', topProjects);
      setProjects(topProjects);
      
      return projectsData;
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      console.error('Error details:', error.response?.data || error.message);
      setProjects([]); // Очищаем проекты в случае ошибки
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Fetch project statistics by status
  const fetchProjectStats = async () => {
    try {
      // Show loading state
      setProjectStats(prev => ({
        new: { ...prev.new, loading: true },
        pending: { ...prev.pending, loading: true },
        communication: { ...prev.communication, loading: true },
        pendingPayment: { ...prev.pendingPayment, loading: true },
        inProcessing: { ...prev.inProcessing, loading: true },
        closed: { ...prev.closed, loading: true }
      }));
      
      // Get all projects for all time (no date filtering)
      const allProjectsResponse = await axios.get('/api/requests');
      const allProjects = allProjectsResponse.data || [];
      
      // Calculate counts by status for all time
      const newCount = allProjects.filter(p => p.status === 'New').length;
      const pendingCount = allProjects.filter(p => p.status === 'Pending').length;
      const communicationCount = allProjects.filter(p => p.status === 'Communication').length;
      const pendingPaymentCount = allProjects.filter(p => p.status === 'Pending payment').length;
      const inProcessingCount = allProjects.filter(p => p.status === 'In processing').length;
      const closedCount = allProjects.filter(p => p.status === 'Closed').length;
      
      // Get today's projects for change indicator
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const todayProjects = allProjects.filter(project => {
        const projectDate = new Date(project.createdAt);
        return projectDate >= today && projectDate < tomorrow;
      });
      
      // Calculate today's counts by status
      const todayNewCount = todayProjects.filter(p => p.status === 'New').length;
      const todayPendingCount = todayProjects.filter(p => p.status === 'Pending').length;
      const todayCommunicationCount = todayProjects.filter(p => p.status === 'Communication').length;
      const todayPendingPaymentCount = todayProjects.filter(p => p.status === 'Pending payment').length;
      const todayInProcessingCount = todayProjects.filter(p => p.status === 'In processing').length;
      const todayClosedCount = todayProjects.filter(p => p.status === 'Closed').length;
      
      // Calculate total for rate calculation
      const totalCount = newCount + pendingCount + communicationCount + pendingPaymentCount + inProcessingCount + closedCount;
      
      // Update project stats with all-time counts and today's changes
      setProjectStats({
        new: { 
          count: newCount, 
          rate: calculateRate(newCount, totalCount),
          change: todayNewCount,
          loading: false
        },
        pending: { 
          count: pendingCount, 
          rate: calculateRate(pendingCount, totalCount),
          change: todayPendingCount,
          loading: false
        },
        communication: { 
          count: communicationCount, 
          rate: calculateRate(communicationCount, totalCount),
          change: todayCommunicationCount,
          loading: false
        },
        pendingPayment: {
          count: pendingPaymentCount,
          rate: calculateRate(pendingPaymentCount, totalCount),
          change: todayPendingPaymentCount,
          loading: false
        },
        inProcessing: { 
          count: inProcessingCount, 
          rate: calculateRate(inProcessingCount, totalCount),
          change: todayInProcessingCount,
          loading: false
        },
        closed: { 
          count: closedCount, 
          rate: calculateRate(closedCount, totalCount),
          change: todayClosedCount,
          loading: false
        }
      });
    } catch (error) {
      console.error('Failed to fetch project stats:', error);
      
      // Reset loading state on error
      setProjectStats(prev => ({
        new: { ...prev.new, loading: false },
        pending: { ...prev.pending, loading: false },
        communication: { ...prev.communication, loading: false },
        pendingPayment: { ...prev.pendingPayment, loading: false },
        inProcessing: { ...prev.inProcessing, loading: false },
        closed: { ...prev.closed, loading: false }
      }));
    }
  };

  // Fetch financial statistics
  const fetchFinancialStats = async () => {
    try {
      // Show loading state
      setFinancialStats(prev => ({
        paid: { ...prev.paid, loading: true },
        pendingPayment: { ...prev.pendingPayment, loading: true }
      }));
      
      // Get all projects without date filtering
      const response = await axios.get('/api/requests');
      const allProjects = response.data || [];
      
      // Calculate total paid and pending amounts from all projects
      const totalPaid = allProjects
        .filter(p => p.paymentStatus === 'Paid' && p.price)
        .reduce((sum, p) => sum + Number(p.price), 0);
      
      const totalPending = allProjects
        .filter(p => p.status === 'Pending payment' && p.price)
        .reduce((sum, p) => sum + Number(p.price), 0);
      
      // Calculate total amounts for rate calculation
      const totalAmount = totalPaid + totalPending;
      
      // Get today's projects for change indicator
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const todayProjects = allProjects.filter(project => {
        const projectDate = new Date(project.createdAt);
        return projectDate >= today && projectDate < tomorrow;
      });
      
      // Calculate today's paid and pending amounts
      const todayPaid = todayProjects
        .filter(p => p.paymentStatus === 'Paid' && p.price)
        .reduce((sum, p) => sum + Number(p.price), 0);
      
      const todayPending = todayProjects
        .filter(p => p.status === 'Pending payment' && p.price)
        .reduce((sum, p) => sum + Number(p.price), 0);
      
      // Update financial stats with all-time amounts and today's changes
      setFinancialStats({
        paid: {
          amount: totalPaid,
          rate: calculateRate(totalPaid, totalAmount),
          change: todayPaid,
          loading: false,
          currency: '€'
        },
        pendingPayment: {
          amount: totalPending,
          rate: calculateRate(totalPending, totalAmount),
          change: todayPending,
          loading: false,
          currency: '€'
        }
      });
    } catch (error) {
      console.error('Failed to fetch financial stats:', error);
      
      // Reset loading state on error
      setFinancialStats(prev => ({
        paid: { ...prev.paid, loading: false },
        pendingPayment: { ...prev.pendingPayment, loading: false }
      }));
    }
  };

  // Update data when component loads - no date range dependency
  useEffect(() => {
    fetchProjectStats();
    fetchFinancialStats();
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial project dashboard load
  }, []);

  const renderStatBox = (title, value, rate, change, loading) => (
    <StatCard
      title={title}
      icon={projectStatusIcons[title]}
      value={value}
      rate={rate}
      loading={loading}
    >
      {change !== 0 && !loading && (
        <Tooltip title="Change today" arrow>
          <Box sx={changeIndicatorSx(change > 0)}>
            {change > 0 ? '+' : ''}{change}
          </Box>
        </Tooltip>
      )}
    </StatCard>
  );

  return (
    <Box sx={{ mt: 1 }}>
      {/* Project Status Statistics */}
      <Grid container spacing={2} sx={{ mb: 2, mt: 0 }}>
        {/* NEW */}
        <Grid item xs={12} sm={6} md={2}>
          {renderStatBox(
            'NEW', 
            projectStats.new.count, 
            projectStats.new.rate, 
            projectStats.new.change,
            projectStats.new.loading
          )}
        </Grid>

        {/* PENDING */}
        <Grid item xs={12} sm={6} md={2}>
          {renderStatBox(
            'PENDING', 
            projectStats.pending.count, 
            projectStats.pending.rate, 
            projectStats.pending.change,
            projectStats.pending.loading
          )}
        </Grid>

        {/* COMMUNICATION */}
        <Grid item xs={12} sm={6} md={2}>
          {renderStatBox(
            'COMMUNICATION', 
            projectStats.communication.count, 
            projectStats.communication.rate, 
            projectStats.communication.change,
            projectStats.communication.loading
          )}
        </Grid>

        {/* PENDING PAYMENT */}
        <Grid item xs={12} sm={6} md={2}>
          {renderStatBox(
            'PENDING PAYMENT', 
            projectStats.pendingPayment.count, 
            projectStats.pendingPayment.rate, 
            projectStats.pendingPayment.change,
            projectStats.pendingPayment.loading
          )}
        </Grid>

        {/* IN PROCESSING */}
        <Grid item xs={12} sm={6} md={2}>
          {renderStatBox(
            'IN PROCESSING', 
            projectStats.inProcessing.count, 
            projectStats.inProcessing.rate, 
            projectStats.inProcessing.change,
            projectStats.inProcessing.loading
          )}
        </Grid>

        {/* CLOSED */}
        <Grid item xs={12} sm={6} md={2}>
          {renderStatBox(
            'CLOSED', 
            projectStats.closed.count, 
            projectStats.closed.rate, 
            projectStats.closed.change,
            projectStats.closed.loading
          )}
        </Grid>
      </Grid>

      {/* Projects and Financial Stats */}
      <Grid container spacing={2}>
        {/* Projects Table */}
        <Grid item xs={12} md={8}>
          <Card sx={panelCardSx}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: acme.colors.foreground }}
            >
              PROJECTS
            </Typography>
            <Typography sx={{ mb: 3, fontSize: '0.875rem', color: acme.colors.mutedForegroundSolid }}>
              Top projects sorted by completion percentage
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} sx={{ color: acme.colors.iconDefault }} />
              </Box>
            ) : (
              <>
                <Table containerSx={{ boxShadow: 'none', border: 'none' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell width="5%">#</TableCell>
                        <TableCell width="25%">company</TableCell>
                        <TableCell width="30%">project name</TableCell>
                        <TableCell width="25%">progress</TableCell>
                        <TableCell width="15%">percent</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {projects.length > 0 ? (
                        projects.map((project, index) => {
                          const progressPercent = calculateProgress(project.status);
                          return (
                            <TableRow key={project._id || index} sx={{ '& td': { py: 1 } }}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{project.companyName || 'WeInnes'}</TableCell>
                              <TableCell>{project.projectName || `Project ${index + 1}`}</TableCell>
                              <TableCell>
                                <LinearProgress variant="determinate" value={progressPercent} sx={progressBarSx} />
                              </TableCell>
                              <TableCell>{progressPercent}%</TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} style={{ border: 'none', padding: 0 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 6,
                                px: 4,
                                textAlign: 'center',
                              }}
                            >
                              <ClipboardList size={48} strokeWidth={1.5} color={acme.colors.iconMuted} />
                              <Typography sx={{ fontWeight: 600, color: acme.colors.foreground, mt: 2, mb: 0.5 }}>
                                No projects found
                              </Typography>
                              <Typography sx={{ fontSize: '0.875rem', color: acme.colors.mutedForegroundSolid, maxWidth: 300 }}>
                                Your projects will appear here once you create them
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                </Table>
                
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button variant="outline" onClick={() => navigate('/requests')}>
                    All Projects
                  </Button>
                </Box>
              </>
            )}
            </CardContent>
          </Card>
        </Grid>

        {/* Financial Stats */}
        <Grid item xs={12} md={4} sx={{ display: 'flex', height: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
            {/* PAID */}
            <Card sx={{ ...panelCardSx, flex: 1, mb: 2 }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DashboardMetricIcon name="projectPendingPayment" size={20} tone="success" />
                  <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>PAID</Typography>
                </Box>
                {financialStats.paid.change !== 0 && !financialStats.paid.loading && (
                  <Box sx={changeIndicatorSx(financialStats.paid.change > 0)}>
                    {financialStats.paid.change > 0 ? '+' : ''}{financialStats.paid.change}
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 3 }}>
                {financialStats.paid.loading ? (
                  <CircularProgress size={28} sx={{ color: acme.colors.iconDefault }} />
                ) : (
                  <Typography sx={{ fontSize: { xs: '2rem', sm: '2.5rem' }, fontWeight: 700 }}>
                    {financialStats.paid.amount}{financialStats.paid.currency}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: '0.875rem', color: acme.colors.mutedForegroundSolid, textAlign: 'center' }}>
                Total
              </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{ ...panelCardSx, flex: 1 }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DashboardMetricIcon name="projectPendingPayment" size={20} tone="default" />
                  <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>PENDING PAYMENT</Typography>
                </Box>
                {financialStats.pendingPayment.change !== 0 && !financialStats.pendingPayment.loading && (
                  <Box sx={changeIndicatorSx(financialStats.pendingPayment.change > 0)}>
                    {financialStats.pendingPayment.change > 0 ? '+' : ''}{financialStats.pendingPayment.change}
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 3 }}>
                {financialStats.pendingPayment.loading ? (
                  <CircularProgress size={28} sx={{ color: acme.colors.iconDefault }} />
                ) : (
                  <Typography sx={{ fontSize: { xs: '2rem', sm: '2.5rem' }, fontWeight: 700 }}>
                    {financialStats.pendingPayment.amount}{financialStats.pendingPayment.currency}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: '0.875rem', color: acme.colors.mutedForegroundSolid, textAlign: 'center' }}>
                Total
              </Typography>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardProject; 